'use client';

/**
 * DashboardRefreshControl — Phase F2 Sprint 1.
 *
 * Combined "Refresh now" button + auto-refresh interval picker.
 * Closes another universal Phase C audit gap: 0/6 dashboards had any
 * refresh affordance — users had to reload the page to see new data.
 *
 * Consumer flow:
 *   const [autoRefreshSec, setAutoRefreshSec] = useState<number | null>(null);
 *   useEffect(() => {
 *     if (!autoRefreshSec) return;
 *     const id = setInterval(() => refetch(), autoRefreshSec * 1000);
 *     return () => clearInterval(id);
 *   }, [autoRefreshSec, refetch]);
 *   <DashboardRefreshControl
 *     onRefresh={refetch}
 *     intervalSeconds={autoRefreshSec}
 *     onIntervalChange={setAutoRefreshSec}
 *     lastRefreshedAt={lastFetchedAt}
 *   />
 *
 * The component is pure UI + callbacks — the consumer owns the timer.
 * This keeps the primitive testable + lets the consumer pause / restart
 * the interval on tab visibility changes if needed.
 */

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, RefreshCw } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import type { ControlSize } from '../control-styles';

const INTERVAL_OPTIONS: ReadonlyArray<{ id: number | null; label: string }> = [
  { id: null, label: 'Off' },
  { id: 30, label: '30s' },
  { id: 60, label: '1m' },
  { id: 300, label: '5m' },
  { id: 900, label: '15m' },
];

export interface DashboardRefreshControlProps {
  size?: ControlSize;
  /** Fired when the user clicks "Refresh now". */
  onRefresh: () => void | Promise<void>;
  /** Current auto-refresh interval in seconds, or null for off. */
  intervalSeconds: number | null;
  /** Fired when the user picks a different auto-refresh interval. */
  onIntervalChange: (next: number | null) => void;
  /** Optional "last refreshed" timestamp shown beside the button. */
  lastRefreshedAt?: Date | null;
  /** Disable interactions (e.g. while initial load is pending). */
  disabled?: boolean;
  /** Show a spinner inside the button while a refresh is in flight. */
  isRefreshing?: boolean;
  className?: string;
}

export function DashboardRefreshControl({
  size = 'toolbar',
  onRefresh,
  intervalSeconds,
  onIntervalChange,
  lastRefreshedAt,
  disabled,
  isRefreshing,
  className,
}: DashboardRefreshControlProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handle(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const intervalLabel = INTERVAL_OPTIONS.find((o) => o.id === intervalSeconds)?.label ?? 'Off';

  return (
    <div
      ref={rootRef}
      className={cn('inline-flex items-center gap-1.5', className)}
      data-dashboard-refresh-control
    >
      <Button
        variant="outline"
        size={size}
        type="button"
        onClick={() => onRefresh()}
        disabled={disabled || isRefreshing}
        aria-label="Refresh dashboard data"
      >
        <RefreshCw className={cn('h-3 w-3', isRefreshing && 'animate-spin')} aria-hidden="true" />
        Refresh
      </Button>

      <div className="relative">
        <Button
          variant="outline"
          size={size}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label="Auto-refresh interval"
        >
          Auto · {intervalLabel}
          <ChevronDown className="h-3 w-3" aria-hidden="true" />
        </Button>
        {open && (
          <div
            role="menu"
            aria-label="Auto-refresh interval menu"
            className="absolute right-0 z-30 mt-1 w-32 rounded-md border border-slate-200 bg-white p-1 shadow-lg"
          >
            {INTERVAL_OPTIONS.map((opt) => {
              const active = opt.id === intervalSeconds;
              return (
                <button
                  key={String(opt.id)}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => {
                    onIntervalChange(opt.id);
                    setOpen(false);
                  }}
                  className={cn(
                    'block w-full rounded px-2 py-1 text-left text-[11.5px]',
                    active
                      ? 'bg-statura-green-accessible/10 font-semibold text-statura-ink'
                      : 'text-statura-ink hover:bg-slate-50',
                  )}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {lastRefreshedAt && (
        <span
          className="text-[10.5px] text-text-muted"
          aria-label={`Last refreshed ${lastRefreshedAt.toISOString()}`}
        >
          Last: {formatRelative(lastRefreshedAt)}
        </span>
      )}
    </div>
  );
}

function formatRelative(date: Date): string {
  const ms = Date.now() - date.getTime();
  const sec = Math.floor(ms / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return date.toLocaleString();
}
